import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Tag } from "lucide-react";
import { api, fmtDate } from "@/lib/api";
import { useLang, usePick } from "@/i18n/LanguageContext";
import Reveal, { Caption } from "@/components/Reveal";

export function BlogList() {
  const { t } = useLang();
  const pick = usePick();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/posts").then((r) => setPosts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const [featured, ...rest] = posts;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 sm:py-24" data-testid="blog-page">
      <Reveal>
        <Caption>{t("blog.caption")}</Caption>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight mt-4 text-ink max-w-3xl">
          {t("blog.titleA")} <span className="italic text-terracotta">{t("blog.titleB")}</span>
        </h1>
        <p className="text-ink/60 max-w-2xl mt-6 leading-relaxed">{t("blog.copy")}</p>
      </Reveal>

      {loading ? (
        <p className="mt-16 text-ink/40 text-sm" data-testid="blog-loading">{t("blog.loading")}</p>
      ) : (
        <>
          {featured && (
            <Reveal className="mt-12">
              <Link
                to={`/blog/${featured.slug}`}
                data-testid={`featured-post-${featured.slug}`}
                className="group grid grid-cols-1 lg:grid-cols-2 gap-8 items-center bg-white rounded-3xl overflow-hidden border border-terracotta/10 shadow-[0_2px_24px_rgba(22,27,34,0.07)]"
              >
                <div className="h-72 lg:h-[420px] overflow-hidden">
                  <img src={featured.image} alt={pick(featured, "title")} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-8 lg:pr-12">
                  <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-terracotta">{featured.category} · {fmtDate(featured.created_at)}</p>
                  <h2 className="font-serif text-3xl lg:text-4xl leading-tight mt-4 text-ink group-hover:text-terracotta transition-colors">{pick(featured, "title")}</h2>
                  <p className="text-ink/60 leading-relaxed mt-4">{pick(featured, "excerpt")}</p>
                  <span className="inline-block mt-6 text-sm font-semibold text-terracotta">{t("blog.readStory")}</span>
                </div>
              </Link>
            </Reveal>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8" data-testid="blog-grid">
            {rest.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.08}>
                <Link to={`/blog/${p.slug}`} data-testid={`post-card-${p.slug}`} className="group block">
                  <div className="rounded-3xl overflow-hidden h-56 mb-5">
                    <img src={p.image} alt={pick(p, "title")} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-terracotta">{p.category} · {fmtDate(p.created_at)}</p>
                  <h3 className="font-serif text-2xl leading-snug mt-2 text-ink group-hover:text-terracotta transition-colors">{pick(p, "title")}</h3>
                  <p className="text-sm text-ink/55 mt-2 leading-relaxed line-clamp-2">{pick(p, "excerpt")}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const { t } = useLang();
  const pick = usePick();
  const [post, setPost] = useState(null);
  const [others, setOthers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api.get(`/posts/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    api.get("/posts").then((r) => setOthers(r.data.filter((p) => p.slug !== slug).slice(0, 2))).catch(() => {});
  }, [slug]);

  if (loading) {
    return <div className="max-w-3xl mx-auto px-4 py-32 text-ink/40 text-sm" data-testid="post-loading">{t("blog.opening")}</div>;
  }
  if (notFound || !post) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-32 text-center" data-testid="post-not-found">
        <h1 className="font-serif text-4xl text-ink">{t("blog.notFound")}</h1>
        <Link to="/blog" data-testid="post-back-link" className="inline-block mt-6 text-sm font-semibold text-terracotta">{t("blog.notFoundBack")}</Link>
      </div>
    );
  }

  return (
    <article data-testid="blog-post-page">
      <div className="relative h-[45vh] min-h-[320px] overflow-hidden">
        <img src={post.image} alt={pick(post, "title")} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-night/70 to-night/10" />
      </div>
      <div className="max-w-3xl mx-auto px-4 sm:px-8 -mt-24 relative z-10">
        <Reveal className="bg-sand rounded-3xl shadow-xl border border-terracotta/10 p-8 sm:p-12">
          <Link to="/blog" data-testid="post-back-btn" className="inline-flex items-center gap-2 text-sm font-semibold text-terracotta hover:text-terracotta-dark transition-colors mb-6">
            <ArrowLeft size={15} /> {t("blog.back")}
          </Link>
          <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-terracotta">{post.category} · {fmtDate(post.created_at)}</p>
          <h1 className="font-serif text-3xl sm:text-5xl leading-tight text-ink mt-4" data-testid="post-title">{pick(post, "title")}</h1>
          <div className="flex flex-wrap gap-2 mt-6">
            {(post.tags || []).map((tag) => (
              <span key={tag} className="inline-flex items-center gap-1.5 rounded-full bg-teal/10 text-teal px-3 py-1 text-xs font-medium">
                <Tag size={11} /> {tag}
              </span>
            ))}
          </div>
          <div className="mt-8 space-y-5 text-ink/75 leading-[1.85] text-[1.05rem]" data-testid="post-content">
            {(pick(post, "content") || "").split(/\n+/).filter(Boolean).map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </Reveal>

        {others.length > 0 && (
          <div className="py-16">
            <Caption>{t("blog.keepReading")}</Caption>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-6">
              {others.map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`related-post-${p.slug}`} className="group flex gap-4 items-center bg-white rounded-3xl p-4 border border-terracotta/10 hover:-translate-y-1 transition-transform">
                  <img src={p.image} alt="" loading="lazy" className="w-24 h-24 rounded-2xl object-cover shrink-0" />
                  <div>
                    <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-terracotta">{p.category}</p>
                    <h3 className="font-serif text-lg leading-snug text-ink group-hover:text-terracotta transition-colors mt-1">{pick(p, "title")}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </article>
  );
}
